// Builds `validateSearch` functions for route definitions.
//
// The blanket `validateSearchStrings` this replaces asserted a per-route type
// over a `String()`-everything coercion: any value passed, and the declared type
// was a claim rather than a check. A schema declares each key it owns, so the
// type it produces is derived from validators that actually ran.
//
// Four rules, each forced by how router-core assembles a match's search
// (`router.cjs:863-878`):
//
//   preMatchSearch  = { ...parentSearch, ...validatorOutput }
//   _strictSearch   = { ...parentStrictSearch, ...validatorOutput }
//
//  1. **Only declared keys are emitted.** Undeclared keys still reach the page:
//     they come in via `...parentSearch`, already strings (the router's
//     `parseSearch` produces nothing else). `usePersistedParams` and
//     `GlobalOverlays` still read some route-agnostic values from that
//     passthrough, and neither is affected by what a schema declares.
//
//  2. **A key absent from the URL is not emitted at all** — not even as
//     `undefined`. Spreading `{ key: undefined }` still creates an *own*
//     property, and `usePersistedParams` decides whether the URL owns a value
//     with `hasOwnProperty` (`usePersistedParams.ts:73`). A phantom key there
//     would beat the cookie layer with `undefined` and wipe the stored value for
//     the dual-backed keys. This is also why no validator may supply a default:
//     a default is a phantom key that then gets serialized into the URL on the
//     next navigation.
//
//  3. **A key that is present but invalid is emitted as `undefined`.** Deleting
//     it does nothing — `...parentSearch` merges the raw value straight back in.
//     Assigning `undefined` is what overrides it. The invalid value stays in the
//     address bar until the next same-route navigation, which drops it.
//
//  4. **A validator never throws.** router-core catches the throw and marks the
//     match `status: "error"`, so one mistyped query param would replace the
//     page with an error boundary. Rejecting a value means returning `undefined`.

// Returns the coerced value, or `undefined` to reject the raw one.
export type SearchField<Value> = (raw: unknown) => Value | undefined

// Every search value arrives as a string (see `parseSearch`), so this is the
// identity case: it exists to declare that a key belongs to the route.
export const text: SearchField<string> = (raw) => {
  if (raw === undefined || raw === null) {
    return undefined
  }
  return String(raw)
}

// Narrows to a fixed set. Unlike the asserted union types this replaces, a value
// outside the set is rejected rather than passed through wearing the type.
export const oneOf =
  <const Allowed extends string>(allowed: readonly Allowed[]): SearchField<Allowed> =>
  (raw) => {
    if (raw === undefined || raw === null) {
      return undefined
    }
    const value = String(raw)
    return allowed.includes(value as Allowed) ? (value as Allowed) : undefined
  }

type Fields = Record<string, SearchField<unknown>>

type Declared<F extends Fields> = {
  [Key in keyof F]?: F[Key] extends SearchField<infer Value> ? Value : never
}

// Exactly the declared keys, and nothing else.
//
// Intersecting a `Record<string, string | undefined>` here would let a page read
// an undeclared key without a type error, but it costs more than it gives: the
// index signature propagates into the router's registered search types, so every
// `navigate({ to: ".", search: updater })` in the app has to widen its updater to
// match. It also defeats the purpose — an undeclared key reaching a page
// unnoticed is exactly the defect this replaces (`blankSlate` and the check-in
// occurrences date range were both read off routes that never declared them).
//
// So a page that needs a key declares it. Route-agnostic consumers that cannot
// know the key set — `GlobalOverlays`, the persisted-params blob,
// `buildPath`'s param overflow — keep reading the permissive
// `useCurrentSearch()`, which is unaffected: rule 1 leaves their keys on
// `match.search` untouched.
export type SearchOf<F extends Fields> = Declared<F>

export const searchSchema =
  <F extends Fields>(fields: F) =>
  (search: Record<string, unknown>): SearchOf<F> => {
    const validated: Record<string, unknown> = {}

    for (const key in fields) {
      // Rule 2: presence in the URL is what decides whether the key is emitted.
      if (Object.prototype.hasOwnProperty.call(search, key)) {
        // Rule 3: `undefined` for a rejected value, so the merge overrides it.
        validated[key] = fields[key](search[key])
      }
    }

    return validated as SearchOf<F>
  }
